/**
 * Per-member accordion card for the combined report (the "Individual Flags"
 * pattern): one row per household member, a right-hand summary, and an
 * expandable body that renders the member's section EXACTLY as the
 * individual page does. Open state is plain useState; no effects.
 */
import { ChevronDown } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import type { GroupMember } from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import { DETAIL_CARD } from '../primitives'

export interface MemberAccordionRow {
	/** Right-hand summary beside the chevron (counts, a date, "No flags"). */
	summary: ReactNode
	/** false → the row is inert (no chevron, not clickable). */
	expandable: boolean
	/** The expanded body — the member's individual-page section body. */
	body: ReactNode
}

export function MemberAccordion({
	title,
	icon,
	members,
	row,
}: {
	title: string
	/** Section glyph rendered before the title (SectionIcon / IconSquare);
	 * omitted → the plain heading the Individual Flags card has always used. */
	icon?: ReactNode
	members: GroupMember[]
	row: (member: GroupMember) => MemberAccordionRow
}) {
	const [openId, setOpenId] = useState<string | null>(null)
	if (members.length === 0) return null

	return (
		<section className={cn(DETAIL_CARD, 'p-6 sm:p-7')}>
			<div className="flex items-center gap-3">
				{icon}
				<h2 className="text-[16px] font-semibold text-[#32302F]">{title}</h2>
			</div>
			<div className="mt-4 divide-y divide-[#F0F0EE]">
				{members.map((member) => {
					const { summary, expandable, body } = row(member)
					const open = expandable && openId === member.applicationId
					return (
						<div key={member.applicationId}>
							<button
								type="button"
								disabled={!expandable}
								aria-expanded={expandable ? open : undefined}
								onClick={() => setOpenId(open ? null : member.applicationId)}
								className={cn(
									'flex w-full items-center justify-between gap-4 py-4 text-left',
									expandable ? 'cursor-pointer' : 'cursor-default',
								)}
							>
								<span className="text-[15px] font-medium text-[#32302F]">
									{member.displayName}
								</span>
								<span className="flex items-center gap-4">
									{summary}
									{expandable ? (
										<ChevronDown
											aria-hidden="true"
											className={cn(
												'size-4 text-[#747474] transition-transform',
												open && 'rotate-180',
											)}
										/>
									) : null}
								</span>
							</button>
							{open ? body : null}
						</div>
					)
				})}
			</div>
		</section>
	)
}
